interface Position {
  x: number;
  y: number;
}

interface MoveResult {
  newX: number;
  newY: number;
  merged: Tile | null;
}

class Tile {
  public element: HTMLDivElement;

  constructor(
    public value: number,
    public x: number,
    public y: number
  ) {
    this.element = document.createElement('div');
    this.setupElement();
  }

  private setupElement(): void {
    this.element.className = 'tile';
    this.element.textContent = this.value.toString();
    this.element.style.cssText = `
      position: absolute;
      width: calc(25% - 10px);
      height: calc(25% - 10px);
      background: ${this.getBackgroundColor()};
      color: ${this.value <= 4 ? '#776e65' : '#f9f6f2'};
      font-size: ${this.value < 1000 ? '40px' : '35px'};
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 5px;
      transition: all 0.15s ease-in-out;
      left: calc(${this.x * 25}% + 5px);
      top: calc(${this.y * 25}% + 5px);
      z-index: 1;
    `;
  }

  private getBackgroundColor(): string {
    const colors: { [key: number]: string } = {
      2: '#eee4da',
      4: '#ede0c8',
      8: '#f2b179',
      16: '#f59563',
      32: '#f67c5f',
      64: '#f65e3b',
      128: '#edcf72',
      256: '#edcc61',
      512: '#edc850',
      1024: '#edc53f'
    };
    return colors[this.value] || '#edc53f';
  }

  updatePosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.element.style.left = `calc(${x * 25}% + 5px)`;
    this.element.style.top = `calc(${y * 25}% + 5px)`;
  }

  merge(otherTile: Tile): number {
    this.value *= 2;
    this.element.textContent = this.value.toString();
    this.element.style.backgroundColor = this.getBackgroundColor();
    this.element.style.color = this.value <= 4 ? '#776e65' : '#f9f6f2';
    return this.value;
  }
}

export class Game {
  private grid: (Tile | null)[][];
  private score: number;
  private gameContainer: HTMLElement;
  private scoreElement: HTMLElement;
  private muteButton: HTMLElement;
  private restartButton: HTMLElement;
  private isMuted: boolean;
  private moveSound!: HTMLAudioElement;
  private mergeSound!: HTMLAudioElement;
  private gameOverSound!: HTMLAudioElement;
  private winSound!: HTMLAudioElement;

  constructor() {
    this.grid = Array(4).fill(null).map(() => Array(4).fill(null));
    this.score = 0;
    this.gameContainer = document.getElementById('game-container')!;
    this.scoreElement = document.getElementById('score')!;
    this.muteButton = document.getElementById('mute-btn') || document.createElement('div');
    this.restartButton = document.getElementById('restart-btn')!;
    this.isMuted = false;
    this.setupAudio();
    this.setupEventListeners();
    this.initializeGame();
    this.setupGameOverStyle();
  }

  private setupAudio(): void {
    this.moveSound = new Audio('move.mp3');
    this.mergeSound = new Audio('merge.mp3');
    this.gameOverSound = new Audio('gameover.mp3');
    this.winSound = new Audio('win.mp3');

    if (this.muteButton instanceof HTMLElement) {
      this.muteButton.addEventListener('click', () => {
        this.isMuted = !this.isMuted;
        this.muteButton.textContent = this.isMuted ? '🔇' : '🔊';
      });
    }
  }

  private playSound(sound: HTMLAudioElement): void {
    if (!this.isMuted && sound) {
      sound.currentTime = 0;
      sound.play().catch(() => {});
    }
  }

  private setupEventListeners(): void {
    document.addEventListener('keydown', (e: KeyboardEvent) => this.handleInput(e));
    let touchStartX: number, touchStartY: number;

    this.gameContainer.addEventListener('touchstart', (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    });

    this.gameContainer.addEventListener('touchend', (e: TouchEvent) => {
      if (!touchStartX || !touchStartY) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - touchStartX;
      const deltaY = touchEndY - touchStartY;

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > 0) this.move('right');
        else this.move('left');
      } else {
        if (deltaY > 0) this.move('down');
        else this.move('up');
      }
    });

    if (this.restartButton) {
      this.restartButton.addEventListener('click', () => this.restart());
    }
  }

  private handleInput(e: KeyboardEvent): void {
    switch(e.key) {
      case 'ArrowUp': this.move('up'); break;
      case 'ArrowDown': this.move('down'); break;
      case 'ArrowLeft': this.move('left'); break;
      case 'ArrowRight': this.move('right'); break;
    }
  }

  private initializeGame(): void {
    this.addRandomTile();
    this.addRandomTile();
  }

  private addRandomTile(): void {
    const emptyCells: Position[] = [];
    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 4; x++) {
        if (!this.grid[y][x]) emptyCells.push({x, y});
      }
    }

    if (emptyCells.length) {
      const {x, y} = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      const value = Math.random() < 0.9 ? 2 : 4;
      const tile = new Tile(value, x, y);
      this.grid[y][x] = tile;
      this.gameContainer.appendChild(tile.element);
    }
  }

  private move(direction: 'up' | 'down' | 'left' | 'right'): void {
    let moved = false;
    const vectors: { [key: string]: Position } = {
      up: {x: 0, y: -1},
      down: {x: 0, y: 1},
      left: {x: -1, y: 0},
      right: {x: 1, y: 0}
    };

    const vector = vectors[direction];
    const traversals = this.buildTraversals(vector);

    traversals.y.forEach(y => {
      traversals.x.forEach(x => {
        const tile = this.grid[y][x];
        if (tile) {
          const {newX, newY, merged} = this.findFarthestPosition(x, y, vector);

          if (x !== newX || y !== newY) {
            moved = true;
            if (merged) {
              const mergedValue = tile.merge(merged);
              this.score += mergedValue;
              this.scoreElement.textContent = this.score.toString();
              this.playSound(this.mergeSound);
              merged.element.remove();
              this.grid[newY][newX] = null;

              if (mergedValue === 1024) {
                this.win();
              }
            }
            this.grid[y][x] = null;
            this.grid[newY][newX] = tile;
            tile.updatePosition(newX, newY);
          }
        }
      });
    });

    if (moved) {
      this.playSound(this.moveSound);
      this.addRandomTile();
      if (this.isGameOver()) {
        this.gameOver();
      }
    }
  }

  private buildTraversals(vector: Position): { x: number[], y: number[] } {
    const traversals = {
      x: [0, 1, 2, 3],
      y: [0, 1, 2, 3]
    };

    if (vector.x === 1) traversals.x.reverse();
    if (vector.y === 1) traversals.y.reverse();

    return traversals;
  }

  private findFarthestPosition(x: number, y: number, vector: Position): MoveResult {
    let prevX = x;
    let prevY = y;
    let nextX = x + vector.x;
    let nextY = y + vector.y;

    while (this.isValidPosition(nextX, nextY)) {
      if (this.grid[nextY][nextX]) {
        if (this.grid[nextY][nextX]!.value === this.grid[y][x]!.value) {
          return { newX: nextX, newY: nextY, merged: this.grid[nextY][nextX] };
        }
        break;
      }
      prevX = nextX;
      prevY = nextY;
      nextX += vector.x;
      nextY += vector.y;
    }

    return { newX: prevX, newY: prevY, merged: null };
  }

  private isValidPosition(x: number, y: number): boolean {
    return x >= 0 && x < 4 && y >= 0 && y < 4;
  }

  private isGameOver(): boolean {
    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 4; x++) {
        if (!this.grid[y][x]) return false;

        const directions: Position[] = [{x: 0, y: 1}, {x: 1, y: 0}];
        for (const dir of directions) {
          const newX = x + dir.x;
          const newY = y + dir.y;

          if (this.isValidPosition(newX, newY) &&
              this.grid[newY][newX]?.value === this.grid[y][x]!.value) {
            return false;
          }
        }
      }
    }
    return true;
  }

  private win(): void {
    document.getElementById('win-message')!.style.display = 'flex';
    this.playSound(this.winSound);
  }

  private gameOver(): void {
    document.getElementById('game-over')!.style.display = 'flex';
    this.playSound(this.gameOverSound);
  }

  private setupGameOverStyle(): void {
    const gameOver = document.getElementById('game-over')!;
    const winMessage = document.getElementById('win-message')!;

    gameOver.style.zIndex = '2';
    winMessage.style.zIndex = '2';
  }

  public restart(): void {
    this.grid.forEach((row, y) => {
      row.forEach((tile, x) => {
        if (tile) {
          tile.element.remove();
          this.grid[y][x] = null;
        }
      });
    });

    this.score = 0;
    this.scoreElement.textContent = '0';

    document.getElementById('game-over')!.style.display = 'none';
    document.getElementById('win-message')!.style.display = 'none';

    this.initializeGame();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Game();
});